import { useCallback, useEffect, useState } from 'react';
import { getParameter } from '@hotjar/insights-common/lib/url';
import { useQueryParameter } from '@hotjar/webapp/hooks/useQueryParameter';
import {
  getPageType,
  getRoute,
  getRouteConfigByPath,
  navigateTo,
} from '@hotjar/webapp/utils/routing';
import { browserHistory } from '@hotjar/webapp/utils/routing/browser-history';
import type { UserId } from '@hotjar/user';
import { useOrganizationSizeChoices } from '@hotjar/webapp/hooks/useOrganizationSizeChoices';
import type { OrganizationSizeValue } from '@hotjar/webapp/hooks/useOrganizationSizeChoices';
import type {
  USER_EXPERTISE,
  USER_GOALS,
} from '@hotjar/webapp/domains/activation/user-personalization-info';
import type { Role } from '@hotjar/webapp/features/Activation/OnboardingPage/steps/OrganizationSteps/steps/OrganizationRole';

export interface OnboardingOrgDetails {
  organizationName?: string;
  organizationSize?: OrganizationSizeValue;
  organizationSizeMin?: number;
  phoneNumber?: string;
  goal?: USER_GOALS;
  role?: Role;
  country?: string;
  expertise?: USER_EXPERTISE;
  subscribeToNewsletter?: boolean;
  consentToParentCompanyRules?: boolean;
}

interface OnboardingNavigation {
  clearOrgDetails: (userId: UserId) => void;
  step: string | undefined;
  goToStep: (step: string) => void;
  getOrgDetails: (userId: UserId) => OnboardingOrgDetails | null;
  setOrgDetails: (details: OnboardingOrgDetails, userId: UserId) => void;
}

export enum ONBOARDING_STEPS {
  ORG_NAME = 'org-name',
  ORG_SIZE = 'org-size',
  ROLE = 'role',
  GOAL = 'goal',
  EXPERTISE = 'expertise',
  PHONE_NUMBER = 'phone-number',
}

const ONBOARDING = {
  name: 'onboarding',
  path: '/onboarding?step',
};

function isValidStep(step?: string): boolean {
  return step ? Object.values(ONBOARDING_STEPS).includes(step as ONBOARDING_STEPS) : false;
}

export const useOnboardingNavigation = (): OnboardingNavigation => {
  const routeConfig = getRouteConfigByPath(window.location.pathname);
  const stepFromUrl = useQueryParameter('step') as ONBOARDING_STEPS;
  const organizationSizes = useOrganizationSizeChoices();
  const [step, setStep] = useState<string | undefined>(
    isValidStep(stepFromUrl) ? stepFromUrl : ONBOARDING_STEPS.ORG_NAME,
  );

  const goToStep = useCallback((nextStep) => {
    const currentStep = getParameter(window.location.href, 'step');
    if (nextStep && currentStep !== nextStep) {
      navigateTo(ONBOARDING.path, { step: nextStep });
    }
  }, []);

  const getOrgDetails = (userId: UserId): OnboardingOrgDetails | null => {
    const getOrganizationSizeMinimum = (organizationSizeValue: string): number =>
      organizationSizes.find((orgSize) => organizationSizeValue === orgSize.value)?.min || 0;
    const details = localStorage.getItem(`orgDetails-${userId}`);
    const parsedDetails = details ? JSON.parse(details) : null;

    return parsedDetails
      ? {
          ...parsedDetails,
          organizationSizeMin: getOrganizationSizeMinimum(parsedDetails.organizationSize),
        }
      : null;
  };

  const setOrgDetails = (details: OnboardingOrgDetails, userId: UserId) => {
    if (getOrgDetails(userId)) {
      localStorage.setItem(
        `orgDetails-${userId}`,
        JSON.stringify({ ...getOrgDetails(userId), ...details }),
      );
    } else {
      localStorage.setItem(`orgDetails-${userId}`, JSON.stringify(details));
    }
  };

  const clearOrgDetails = (userId: UserId) => {
    localStorage.removeItem(`orgDetails-${userId}`);
  };

  useEffect(() => {
    if (getPageType(routeConfig?.name ?? '') === ONBOARDING.path && !stepFromUrl) {
      setStep(ONBOARDING_STEPS.ORG_NAME);
      browserHistory.push(`${getRoute(ONBOARDING.path)}?step=${ONBOARDING_STEPS.ORG_NAME}`);
    }
    // useEffect for mounting only to run once
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const urlStep = getParameter(window.location.href, 'step');
    if (isValidStep(urlStep)) {
      setStep(urlStep);
    }
    // href needed here in case it changes via routes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [window.location.href]);

  return { goToStep, getOrgDetails, setOrgDetails, step, clearOrgDetails };
};
